import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const DemoContext = createContext(null);

export const DEMO_BID_TEMPLATE = 'bid-template';

export const DemoProvider = ({ children }) => {
  const [activeDemo, setActiveDemo] = useState(null);
  const [stepIndex, setStepIndex]   = useState(0);
  const [isPaused, setIsPaused]     = useState(false);

  const start = useCallback((demoId = DEMO_BID_TEMPLATE) => {
    setActiveDemo(demoId);
    setStepIndex(0);
    setIsPaused(false);
  }, []);

  const exit = useCallback(() => {
    setActiveDemo(null);
    setStepIndex(0);
    setIsPaused(false);
  }, []);

  const pause   = useCallback(() => setIsPaused(true),  []);
  const resume  = useCallback(() => setIsPaused(false), []);
  const restart = useCallback(() => { setStepIndex(0); setIsPaused(false); }, []);

  const skip = useCallback((total) => {
    setStepIndex((i) => {
      const next = i + 1;
      if (typeof total === 'number' && next >= total) return i;
      return next;
    });
  }, []);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const value = useMemo(() => ({
    activeDemo,
    isActive: !!activeDemo,
    stepIndex,
    setStepIndex,
    isPaused,
    start,
    exit,
    pause,
    resume,
    restart,
    skip,
    back,
  }), [activeDemo, stepIndex, isPaused, start, exit, pause, resume, restart, skip, back]);

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

export const useDemo = () => {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used within a DemoProvider');
  return ctx;
};

export default DemoContext;
